/**
 * SpreadsTotalsTable — moneyline-style table for spread + total markets.
 * Same column structure as GameTable (DATE | TEAM | KALSHI | K VOL | K LIQ |
 * POLY | P VOL | P LIQ); per-event zebra; thick top border between events.
 *
 * Each event renders 5 rows: away team label, spread action row (chip strip +
 * Y/N pills + market data), home team label, total label row, total action
 * row. The team currently on the YES side of the spread ticker (favored at
 * the selected line) is highlighted; the underdog is muted.
 *
 * Spreads share one Kalshi ticker per line (YES = favored team covers); a
 * single Y/N pill pair shows that ticker's prices. Totals share one ticker
 * per strike (YES = Over, NO = Under); a single pair labeled O/U shows
 * both sides at once.
 *
 * Clicking the action row expands a ladder below it. The toolbar YES/NO
 * toggle decides which side's ladder renders — same convention as
 * GameTable's renderLadderRows.
 */

import { Fragment, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/infra/utils';
import { LiquidityBar } from '@/components/atoms/LiquidityBar';
import { Th, Td } from '@/components/atoms/TableCells';
import { queueTone } from '@/components/cards/orderCardModel';
import {
  bookDepthVelocity,
  depthDeltaTone,
  depthKey,
  formatSignedVolume,
} from '@/components/cards/bookDepthVelocity';
import { formatCompactCurrency } from '@/lib/infra/formatters';
import { parseTicker } from '@/lib/kalshiTicker/parse';
import { useMarketStore } from '@/stores/marketStore';
import type { KalshiOrder } from '@/types/kalshi';
import type {
  UserOrderSlice,
  UserPositionSlice,
} from '@/hooks/useUserKalshiPOS';
import type {
  GameEventRow,
  SpreadLine,
  TotalLine,
  MarketSide,
  PriceMode,
  BookLevel,
  VenueBook,
  Sport,
} from '../ValueDashboard/types';
import { americanOdds } from '../ValueDashboard/tradeModalBook';
import { useDisplaySettings } from '@/stores/displaySettings';
import { useFeeFor } from '@/services/hydraFees';
import {
  LADDER_LEVELS,
  bidIsReal,
  effectivePriceForOdds,
  formatVolume,
  pillPriceCents,
  queueDepth,
  orderTooltip,
  type BidRowOrder,
  type FeePerspective,
} from '../ValueDashboard/ladder/helpers';
import { PricePill } from '../ValueDashboard/ladder/PricePill';
import { PosRing } from '../ValueDashboard/ladder/PosRing';
import { GameCountdown, NearEventStartPulse } from '../ValueDashboard/GameTimeIndicators';
import { useGameStateFor } from '@/stores/gameStatesStore';
import { formatGameStatus } from '@/lib/sport/periodLabel';
import { toHydraGameKey } from '@/services/analyticsGameKey';
import { MiniLineStrip } from './MiniLineStrip';
import { linePriceCents, spreadLineKey, totalLineKey } from './synthesize';
import { useMakerRebateActive } from '@/hooks/useMakerRebate';
import { makerRebatePct } from '@/lib/pricing/makerRebate';

/** Look up the live hydra market frame for an order ticker and parse the
 *  canonical line out of its `subtitle` (e.g. "Over 2.5 runs scored",
 *  "Texas wins by over 1.5 runs"). Returns null when no frame is in the
 *  store yet — caller falls back to per-sport strike math. */
function parseLineFromOrderTicker(
  ticker: string,
  _kind: 'spread' | 'total',
): number | null {
  const mkt = useMarketStore.getState().getMarket(ticker);
  const subtitle = mkt?.subtitle;
  if (!subtitle) return null;
  const m = subtitle.match(/\b(?:over|under) (\d+(?:\.\d+)?)\b/i);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

/** Tiny chip-friendly format. Spreads are signed: `+2.5`/`−5.5`. Totals
 *  are positive line magnitudes: `2.5`. Trailing `.0` stripped so whole
 *  half-points render as `+2.5` not `+2.50`. */
function formatOrderLine(line: number, kind: 'spread' | 'total'): string {
  if (kind === 'total') return String(line);
  if (line > 0) return `+${line}`;
  if (line < 0) return `−${Math.abs(line)}`;
  return '0';
}

/** Merge accountStore.openOrders (canonical user-order list, no queue depth)
 *  with snapshot ordersByTicker (queue_position when available) into a
 *  unified `UserOrderSlice[]` the ladder can render. accountStore is the
 *  source-of-truth for "what orders exist"; the snapshot just decorates
 *  each with `queue_position` when the relay returned it. Without this
 *  merge, queue chips silently disappear whenever the snapshot drops a
 *  ticker (large ticker filters, snapshot poll race, etc.). */
function mergeOrdersForTicker(
  ticker: string,
  openOrders: KalshiOrder[],
  ordersByTicker: Map<string, UserOrderSlice[]>,
): UserOrderSlice[] {
  const slicesFromSnapshot = ordersByTicker.get(ticker) ?? [];
  const queueByOrderId = new Map<string, number | null>();
  for (const s of slicesFromSnapshot) queueByOrderId.set(s.orderId, s.queuePosition);

  const out: UserOrderSlice[] = [];
  for (const o of openOrders) {
    if (o.ticker !== ticker) continue;
    if (o.remainingCount <= 0) continue;
    // The user-placed price for a NO order comes back as `noPrice` (cents on
    // the NO side). But Kalshi frequently sends only the YES leg even for a NO
    // order (CLAUDE.md venue quirk), so parseOrders leaves noPrice undefined and
    // the side-specific field alone silently DROPS the order from the overlay.
    // Fall back to the 100− complement of the opposite leg — every sibling
    // order-price site already does (PositionSidebar / orderCardModel / PositionTracker).
    const sidePrice = o.side === 'yes' ? o.yesPrice : o.noPrice;
    const oppPrice = o.side === 'yes' ? o.noPrice : o.yesPrice;
    const priceCents =
      sidePrice != null ? sidePrice : oppPrice != null ? 100 - oppPrice : null;
    if (priceCents == null) continue;
    out.push({
      orderId: o.orderId,
      side: o.side,
      action: o.action,
      priceCents,
      remainingCount: o.remainingCount,
      // Snapshot's queue_position when present; null otherwise (chip will
      // render as `count/?` so the user still sees their working size).
      queuePosition: queueByOrderId.get(o.orderId) ?? null,
    });
  }
  return out;
}

// ── Per-row identification ───────────────────────────────────────────

/** One expandable action row per market type per event. */
export type RowKind = 'spread' | 'total';

interface SpreadsTotalsTableProps {
  rows: GameEventRow[];
  marketSide: MarketSide;
  priceMode: PriceMode;
  /** Per-user Kalshi positions and open orders (keyed by ticker). Drives
   *  the inline 'O'-ring on each action row and the per-order qty/queue
   *  chips on bid rows of the inline ladder. Same source moneyline uses. */
  positionByTicker: Map<string, UserPositionSlice>;
  ordersByTicker: Map<string, UserOrderSlice[]>;
  /** Canonical list of user open orders from accountStore. Walked per
   *  event to aggregate C/O ring counts + line-value chips even on
   *  strikes hydra hasn't surfaced in `rows`. */
  openOrders: KalshiOrder[];
  /** Per-event line index for the spread strip (one shared between
   *  spread-away and spread-home rows). */
  spreadIdxByEvent: Map<string, number>;
  /** Per-event line index for the total strip. */
  totalIdxByEvent: Map<string, number>;
  /** Which row (if any) is currently expanded inline as a ladder. */
  expandedRowByEvent: Map<string, RowKind | null>;
  onSelectSpreadIdx: (eventKey: string, idx: number) => void;
  onSelectTotalIdx: (eventKey: string, idx: number) => void;
  onToggleExpand: (eventKey: string, row: RowKind) => void;
  /** Fired when the user clicks an individual Y/N pill — wires through to
   *  the TradeModal popup. Distinct from `onToggleExpand` so a row-body
   *  click can still toggle the inline ladder without opening the modal. */
  onPillTrade?: (
    eventKey: string,
    row: RowKind,
    venue: 'kalshi' | 'polymarket',
    side: 'yes' | 'no',
    cents: number,
  ) => void;
}

export function SpreadsTotalsTable({
  rows,
  marketSide,
  priceMode,
  positionByTicker,
  ordersByTicker,
  openOrders,
  spreadIdxByEvent,
  totalIdxByEvent,
  expandedRowByEvent,
  onSelectSpreadIdx,
  onSelectTotalIdx,
  onToggleExpand,
  onPillTrade,
}: SpreadsTotalsTableProps) {
  return (
    <div className="bg-card border-border rounded-lg border">
      {/* Title bar — sport derived from the first row. */}
      <div className="border-border flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <h2 className="text-foreground text-lg font-semibold">
            {rows[0]?.sport?.toUpperCase() ?? ''} Spread/Total
          </h2>
          <span className="text-muted-foreground text-xs">&mdash;</span>
          <span className="text-muted-foreground font-mono text-xs">
            {rows[0] ? `KX${rows[0].sport.toUpperCase()}SPREAD / KX${rows[0].sport.toUpperCase()}TOTAL` : ''}
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse border-spacing-0">
          <thead className="bg-muted/50 border-border border-b">
            <tr>
              <Th className="w-28">Date</Th>
              <Th className="w-72">Side &amp; Line</Th>
              <Th className="w-32 text-center">Kalshi</Th>
              <Th
                className="w-16 text-right"
                title="Resting contracts on each side of the book. For totals: O = Over demand, U = Under demand. For spreads: Y = team-covers demand, N = team-doesn't-cover. Cumulative traded volume in tooltip."
              >K Vol</Th>
              <Th className="w-24 text-right" title="Total $ resting in book (bids + asks)">K Liq</Th>
              <Th
                className="w-16 text-right"
                title="Kalshi best-bid depth change over the last 1m on the active side. Positive = building; negative = draining. Empty until 1m of samples accumulates."
              >K Δ/m</Th>
              <Th className="w-32 text-center">Poly</Th>
              <Th
                className="w-16 text-right"
                title="Resting contracts on each side of the book. For totals: O = Over demand, U = Under demand. For spreads: Y = team-covers demand, N = team-doesn't-cover. Cumulative traded volume in tooltip."
              >P Vol</Th>
              <Th className="w-24 text-right" title="Total $ resting in book (bids + asks)">P Liq</Th>
              <Th
                className="w-16 text-right"
                title="Polymarket best-bid depth change over the last 1m on the active side. Pair with K Δ/m for cross-exchange sweep imbalance."
              >P Δ/m</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, eventIndex) => (
              <EventRows
                key={row.key}
                event={row}
                eventIndex={eventIndex}
                marketSide={marketSide}
                priceMode={priceMode}
                positionByTicker={positionByTicker}
                ordersByTicker={ordersByTicker}
                openOrders={openOrders}
                spreadIdx={Math.min(spreadIdxByEvent.get(row.key) ?? 0, Math.max(row.spreads.length - 1, 0))}
                totalIdx={Math.min(totalIdxByEvent.get(row.key) ?? 0, Math.max(row.totals.length - 1, 0))}
                expandedRow={expandedRowByEvent.get(row.key) ?? null}
                onSelectSpreadIdx={(idx) => onSelectSpreadIdx(row.key, idx)}
                onSelectTotalIdx={(idx) => onSelectTotalIdx(row.key, idx)}
                onToggleExpand={(rowKind) => onToggleExpand(row.key, rowKind)}
                onPillTrade={(rowKind, venue, side, cents) =>
                  onPillTrade?.(row.key, rowKind, venue, side, cents)
                }
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── EventRows — 4 main rows + optional ladder for the expanded row ────

export interface EventRowsProps {
  event: GameEventRow;
  eventIndex: number;
  marketSide: MarketSide;
  priceMode: PriceMode;
  positionByTicker: Map<string, UserPositionSlice>;
  ordersByTicker: Map<string, UserOrderSlice[]>;
  openOrders: KalshiOrder[];
  spreadIdx: number;
  totalIdx: number;
  expandedRow: RowKind | null;
  onSelectSpreadIdx: (idx: number) => void;
  onSelectTotalIdx: (idx: number) => void;
  onToggleExpand: (row: RowKind) => void;
  onPillTrade: (row: RowKind, venue: 'kalshi' | 'polymarket', side: 'yes' | 'no', cents: number) => void;
  /** Skip the leading MatchupRow (Date + "Away @ Home"). Consumers like
   *  GameDetail show the matchup + date in the page header already and
   *  the row is redundant noise. ActionRows + ladders still render. */
  hideMatchupRow?: boolean;
}

export function EventRows({
  event,
  eventIndex,
  marketSide,
  priceMode,
  positionByTicker,
  ordersByTicker,
  openOrders,
  spreadIdx,
  totalIdx,
  expandedRow,
  onSelectSpreadIdx,
  onSelectTotalIdx,
  onToggleExpand,
  onPillTrade,
  hideMatchupRow = false,
}: EventRowsProps) {
  const spread: SpreadLine | undefined = event.spreads[spreadIdx];
  const total: TotalLine | undefined = event.totals[totalIdx];
  const isEventExpanded = expandedRow !== null;

  // Maker-fee rebate fraction (0..1) for this event's maker-perspective
  // ladder odds. 0 unless the org is MM-enrolled and the toggle is on; live
  // events rebate 100% of the maker fee, pregame 50%.
  const makerRebateActive = useMakerRebateActive();
  const makerRebateFraction = makerRebateActive
    ? makerRebatePct({ isLive: event.gameState === 'live' })
    : 0;

  // Per-event zebra: every row of one event shares the same background;
  // flip per event.
  const baseRowStyle =
    !isEventExpanded && eventIndex % 2 === 1
      ? { backgroundColor: 'hsl(var(--muted) / var(--zebra-opacity))' }
      : undefined;

  const spreadChips = event.spreads.map((sp) => ({
    key: spreadLineKey(sp),
    label: formatSignedLine(sp.line),
    priceCents: linePriceCents(sp),
  }));
  const totalChips = event.totals.map((t) => ({
    key: totalLineKey(t),
    label: t.line.toString(),
    priceCents: linePriceCents(t),
  }));

  // Aggregate positions + open orders across EVERY line in each strip so the
  // C/O rings under the Spread/Total labels show holdings regardless of which
  // chip is currently selected. Walk `openOrders` directly (canonical
  // accountStore data) and parse each ticker — bets on strikes hydra hasn't
  // surfaced still count toward the ring. (The expanded ladder still wires
  // selected-line orders for per-bid-row queue chips.)
  const stripAggregates = useMemo(() => {
    const spreadTickerSet = new Set(
      event.spreads.flatMap((sp) => (sp.kalshi ? [sp.kalshi.marketTicker] : [])),
    );
    const totalTickerSet = new Set(
      event.totals.flatMap((t) => (t.kalshi ? [t.kalshi.marketTicker] : [])),
    );
    const spreadLineByTicker = new Map<string, number>();
    for (const sp of event.spreads) {
      if (sp.kalshi) spreadLineByTicker.set(sp.kalshi.marketTicker, sp.line);
    }
    const totalLineByTicker = new Map<string, number>();
    for (const t of event.totals) {
      if (t.kalshi) totalLineByTicker.set(t.kalshi.marketTicker, t.line);
    }

    const spreadAgg = { ordersYes: 0, ordersNo: 0, lines: [] as number[], tickers: [] as string[] };
    const totalAgg = { ordersYes: 0, ordersNo: 0, lines: [] as number[], tickers: [] as string[] };

    const sortedEventTeams = [event.awayCode, event.homeCode].sort();

    for (const o of openOrders) {
      if (!o.ticker || o.remainingCount <= 0) continue;
      const ticker = o.ticker;

      // Fast path: order is on a ticker hydra surfaced in this event.
      let line: number | null = null;
      let kind: 'spread' | 'total' | null = null;

      if (spreadTickerSet.has(ticker)) {
        kind = 'spread';
        line = spreadLineByTicker.get(ticker) ?? null;
      } else if (totalTickerSet.has(ticker)) {
        kind = 'total';
        line = totalLineByTicker.get(ticker) ?? null;
      } else {
        // Slow path: parse ticker (venue-agnostic) and match to this
        // event by sport+date+teams. The kalshi spread/total category
        // check below still gates the kalshi-specific outcome math; poly
        // tickers fall through here as untyped for this slow-path slot
        // until the polymarket spread/total row model lands.
        const parsed = parseTicker(ticker);
        if (!parsed.game || !parsed.date) continue;
        if (parsed.date !== event.date) continue;
        const sp = [parsed.game.awayCode, parsed.game.homeCode].sort();
        if (sp[0] !== sortedEventTeams[0] || sp[1] !== sortedEventTeams[1]) continue;

        if (parsed.category === `${event.sport}-spread`) {
          const m = parsed.outcome?.match(/^([A-Z]+)(\d+)$/);
          if (!m) continue;
          const teamCode = m[1];
          const bucket = Number(m[2]);
          const lineMag = parseLineFromOrderTicker(ticker, 'spread') ?? bucket - 0.5;
          const favored: 'away' | 'home' = teamCode === event.awayCode ? 'away' : 'home';
          kind = 'spread';
          line = favored === 'away' ? -lineMag : lineMag;
        } else if (parsed.category === `${event.sport}-total`) {
          const m = parsed.outcome?.match(/^(\d+)$/);
          if (!m) continue;
          const strike = Number(m[1]);
          const minMargin = event.sport === 'mlb' || event.sport === 'wnba';
          kind = 'total';
          line =
            parseLineFromOrderTicker(ticker, 'total') ??
            (minMargin ? strike - 0.5 : strike + 0.5);
        }
      }

      if (!kind) continue;
      const agg = kind === 'spread' ? spreadAgg : totalAgg;
      if (o.side === 'yes') agg.ordersYes += 1;
      else agg.ordersNo += 1;
      if (line !== null && !agg.lines.includes(line)) agg.lines.push(line);
      if (!agg.tickers.includes(ticker)) agg.tickers.push(ticker);
    }

    spreadAgg.lines.sort((a, b) => a - b);
    totalAgg.lines.sort((a, b) => a - b);

    // Positions aggregate by visible-line ticker only — a held contract
    // implies hydra has surfaced its market frame.
    let spreadPosYes = 0, spreadPosNo = 0, totalPosYes = 0, totalPosNo = 0;
    for (const tk of spreadTickerSet) {
      const p = positionByTicker.get(tk);
      if (p) { spreadPosYes += p.yesCount; spreadPosNo += p.noCount; }
    }
    for (const tk of totalTickerSet) {
      const p = positionByTicker.get(tk);
      if (p) { totalPosYes += p.yesCount; totalPosNo += p.noCount; }
    }

    return {
      spread: { ...spreadAgg, posYes: spreadPosYes, posNo: spreadPosNo },
      total: { ...totalAgg, posYes: totalPosYes, posNo: totalPosNo },
    };
  }, [event, openOrders, positionByTicker]);
  const spreadStripAgg = stripAggregates.spread;
  const totalStripAgg = stripAggregates.total;

  const unit = sportUnit(event.sport);
  const spreadDescription = spread
    ? buildSpreadDescription({
        favoredName: spread.favored === 'away' ? event.awayCode : event.homeCode,
        mag: Math.abs(spread.line),
        unit,
      })
    : null;
  const totalDescription = total ? buildTotalDescription({ line: total.line, unit }) : null;

  const isSpreadExpanded = expandedRow === 'spread';
  const isTotalExpanded = expandedRow === 'total';

  return (
    <Fragment>
      {/* Matchup label — "Away @ Home" on one row; favored team brightens
       *  as the user scrolls the spread chips across zero. Tints dark when
       *  the spread is open since matchup + spread row pair on the bet.
       *  Skipped when the parent surface (GameDetail) already shows the
       *  matchup in its page header. */}
      {!hideMatchupRow && (
        <MatchupRow
          eventIndex={eventIndex}
          date={event.date}
          timePt={event.timePt}
          startTimeMs={event.startTimeMs}
          gameState={event.gameState}
          sport={event.sport}
          gameKey={event.key}
          awayCode={event.awayCode}
          homeCode={event.homeCode}
          awayName={event.awayName}
          homeName={event.homeName}
          favored={spread?.favored ?? null}
          rowStyle={baseRowStyle}
          active={isSpreadExpanded}
        />
      )}

      {/* Spread action row — chip strip + Y/N pills + market data. */}
      <ActionRow
        eventIndex={eventIndex}
        chips={spreadChips}
        selectedChipIdx={spreadIdx}
        onSelectChipIdx={onSelectSpreadIdx}
        stripLabel="Spread"
        description={spreadDescription}
        kalshiBook={spread?.kalshi ?? null}
        polyBook={spread?.polymarket ?? null}
        marketSide={marketSide}
        priceMode={priceMode}
        stripPosYes={spreadStripAgg.posYes}
        stripPosNo={spreadStripAgg.posNo}
        stripOrdersYes={spreadStripAgg.ordersYes}
        stripOrdersNo={spreadStripAgg.ordersNo}
        stripOrderLines={spreadStripAgg.lines}
        stripOrderLineKind="spread"
        isExpanded={isSpreadExpanded}
        rowStyle={baseRowStyle}
        onToggle={() => onToggleExpand('spread')}
        onPillTrade={(venue, side, cents) => onPillTrade('spread', venue, side, cents)}
        yesLabel="Y"
        noLabel="N"
      />

      {/* Spread ladder slot — directly below the spread action row. */}
      {isSpreadExpanded && spread && (
        <LadderRowsFor
          venueBookKalshi={spread.kalshi ?? null}
          venueBookPoly={spread.polymarket ?? null}
          flipped={false}
          marketSide={marketSide}
          priceMode={priceMode}
          makerRebatePct={makerRebateFraction}
          userOrders={
            spread.kalshi
              ? mergeOrdersForTicker(spread.kalshi.marketTicker, openOrders, ordersByTicker)
              : null
          }
          onLevelClick={(venue, cents) => onPillTrade('spread', venue, marketSide, cents)}
        />
      )}

      {/* Total action row — chip strip selects the line; O/U pills are
       *  Over/Under prices on that line (O = Over = YES, U = Under = NO).
       *  No dedicated label row; the chip strip itself communicates the
       *  line value. O/U labels carry through to the K Vol / P Vol cell's
       *  stacked depth split (so traders read "O 281K / U 240K" directly
       *  instead of decoding Y/N → Over/Under). */}
      <ActionRow
        eventIndex={eventIndex}
        chips={totalChips}
        selectedChipIdx={totalIdx}
        onSelectChipIdx={onSelectTotalIdx}
        stripLabel="Total"
        description={totalDescription}
        kalshiBook={total?.kalshi ?? null}
        polyBook={total?.polymarket ?? null}
        marketSide={marketSide}
        priceMode={priceMode}
        stripPosYes={totalStripAgg.posYes}
        stripPosNo={totalStripAgg.posNo}
        stripOrdersYes={totalStripAgg.ordersYes}
        stripOrdersNo={totalStripAgg.ordersNo}
        stripOrderLines={totalStripAgg.lines}
        stripOrderLineKind="total"
        isExpanded={isTotalExpanded}
        rowStyle={baseRowStyle}
        onToggle={() => onToggleExpand('total')}
        onPillTrade={(venue, side, cents) => onPillTrade('total', venue, side, cents)}
        yesLabel="O"
        noLabel="U"
      />

      {/* Total ladder slot — directly below the total action row. */}
      {isTotalExpanded && total && (
        <LadderRowsFor
          venueBookKalshi={total.kalshi ?? null}
          venueBookPoly={total.polymarket ?? null}
          flipped={false}
          marketSide={marketSide}
          priceMode={priceMode}
          makerRebatePct={makerRebateFraction}
          userOrders={
            total.kalshi
              ? mergeOrdersForTicker(total.kalshi.marketTicker, openOrders, ordersByTicker)
              : null
          }
          onLevelClick={(venue, cents) => onPillTrade('total', venue, marketSide, cents)}
        />
      )}
    </Fragment>
  );
}

// ── MatchupRow — single "Away @ Home" header row per event ─────────────

interface MatchupRowProps {
  eventIndex: number;
  date: string;
  /** Local-time event start ("7:00p") or empty when unknown. Renders below
   *  the date in the same cell. */
  timePt: string;
  /** Epoch ms event start — drives the pre-game pulse dot + countdown text. */
  startTimeMs: number | null;
  /** Drives the LIVE pulse dot, FINAL chip, and final-row dim. Same
   *  three-state model the moneyline GameTable uses. */
  gameState: 'live' | 'pre' | 'final';
  /** Sport — needed so formatGameStatus can compose the right period
   *  label (Bot 3rd vs Q2 5:32 vs 2nd Set). */
  sport: Sport;
  /** Canonical game key — resolved to the hydra/Unabated game key for
   *  the gameState lookup. */
  gameKey: string;
  /** Team codes — used to build the `/game/:date/:away/:home` URL for
   *  the date-column deep link. */
  awayCode: string;
  homeCode: string;
  awayName: string;
  homeName: string;
  /** Which team is on the YES side of the current spread ticker, if any.
   *  null when there is no spread for this event (totals-only). */
  favored: 'away' | 'home' | null;
  rowStyle: React.CSSProperties | undefined;
  /** True when this event's spread is open — pairs the matchup with the
   *  spread action row visually via the dark tint. */
  active: boolean;
}

function MatchupRow({
  eventIndex,
  date,
  timePt,
  startTimeMs,
  gameState,
  sport,
  gameKey,
  awayCode,
  homeCode,
  awayName,
  homeName,
  favored,
  rowStyle,
  active,
}: MatchupRowProps) {
  const borderClass = eventIndex > 0 ? 'border-t-2 border-border' : '';
  const isFinal = gameState === 'final';
  const isLive = gameState === 'live';
  // Pull Unabated game state for live period text + scores. The poll
  // upstream in SpreadsTotals/index.tsx keeps the store fresh; this
  // hook just reads. Resolves to null pre-game (statusId 1) and when
  // the key isn't in the store yet.
  const hydraGameKey = toHydraGameKey(gameKey);
  const liveGameState = useGameStateFor(hydraGameKey);
  // Show scores any time the game is live or final per Unabated.
  // Falls back to the event.gameState gate when Unabated hasn't
  // populated yet so the row still reads consistently.
  const showScores =
    liveGameState != null &&
    (liveGameState.statusId === 2 || liveGameState.statusId === 3);
  const awayScore = showScores ? liveGameState.awayScore : null;
  const homeScore = showScores ? liveGameState.homeScore : null;
  // Live period label ("Bot 3rd", "Q2 5:32", etc.) replaces the
  // scheduled date+time in the left column once the game starts.
  // Only renders for live (statusId 2) — finals get their FINAL chip,
  // pre-game keeps the date+countdown.
  const liveStatusText =
    liveGameState && liveGameState.statusId === 2
      ? formatGameStatus(liveGameState, sport)
      : null;
  const awayClass =
    favored == null
      ? 'text-foreground'
      : favored === 'away'
        ? 'text-foreground'
        : 'text-muted-foreground/70';
  const homeClass =
    favored == null
      ? 'text-foreground'
      : favored === 'home'
        ? 'text-foreground'
        : 'text-muted-foreground/70';
  return (
    <tr
      className={cn(
        borderClass,
        active && 'bg-black/15 dark:bg-black/95',
        isFinal && 'opacity-60',
      )}
      style={rowStyle}
    >
      <Td className="text-muted-foreground font-mono text-sm">
        <Link
          to={`/game/${date}/${awayCode}/${homeCode}`}
         
          className="hover:text-foreground hover:underline focus:outline-none focus-visible:underline flex flex-col leading-tight"
          title="Open game detail"
        >
          <span className="flex items-center gap-1">
            {isLive ? (
              <span
                className="inline-block h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-green-500"
                title="Live — game in progress"
              />
            ) : (
              !isFinal && startTimeMs != null && (
                <NearEventStartPulse startTimeMs={startTimeMs} />
              )
            )}
            {isFinal ? (
              <span
                className="inline-block rounded border border-zinc-500/60 bg-zinc-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-400"
                title="Game finalized"
              >
                FINAL
              </span>
            ) : (
              date
            )}
          </span>
          {!isFinal && (
            isLive ? (
              // Live → replace the scheduled event start time with the
              // Unabated period status (e.g. "Bot 3rd" for MLB), or an
              // explicit dash when no status feed exists — never the
              // stale start time next to a live dot (Joshua, Jun 6).
              <span className="text-foreground font-mono text-[11px]">
                {liveStatusText ?? '—'}
              </span>
            ) : timePt ? (
              <span className="flex items-center gap-1.5">
                <span className="text-foreground font-mono text-[11px]">
                  {timePt}
                </span>
                {!isLive && startTimeMs != null && (
                  <GameCountdown startTimeMs={startTimeMs} />
                )}
              </span>
            ) : null
          )}
        </Link>
      </Td>
      <Td className="font-medium">
        <span className={awayClass}>{awayName}</span>
        {showScores && awayScore != null && (
          <span className="text-foreground ml-1.5 font-mono tabular-nums">
            ({awayScore})
          </span>
        )}
        <span className="text-muted-foreground/40 mx-2">@</span>
        <span className={homeClass}>{homeName}</span>
        {showScores && homeScore != null && (
          <span className="text-foreground ml-1.5 font-mono tabular-nums">
            ({homeScore})
          </span>
        )}
      </Td>
      <Td colSpan={6} className="py-0.5"> </Td>
    </tr>
  );
}

// ── ActionRow — chip strip + Y/N pills + market data on one row ────────

interface ActionRowProps {
  eventIndex: number;
  chips: Array<{ key: string; label: string; priceCents: number | null }>;
  selectedChipIdx: number;
  onSelectChipIdx: (idx: number) => void;
  stripLabel: string;
  /** Plain-English bet description rendered above the chip strip — e.g.
   *  "St. Louis wins by over 3.5 runs" or "Over 11.5 runs scored". The
   *  selected line value is inlined and highlighted by the helpers. */
  description: React.ReactNode | null;
  kalshiBook: VenueBook | null;
  polyBook: VenueBook | null;
  marketSide: MarketSide;
  priceMode: PriceMode;
  /** Strip-wide aggregates across ALL lines in this strip (not just the
   *  selected chip). Drive the 'C' / 'O' rings under the Spread/Total
   *  label so holdings on any line stay visible regardless of which
   *  chip is currently selected. Counts come from accountStore.openOrders
   *  (stable across hydra ticks); per-bid-row queue chips still source
   *  from ordersByTicker for the selected line only. */
  stripPosYes: number;
  stripPosNo: number;
  stripOrdersYes: number;
  stripOrdersNo: number;
  /** Distinct line values the user has open orders on within this strip,
   *  sorted ascending. Rendered as small chips beside the 'O' ring as
   *  "+2.5  −1.5" (spread, signed) or "2.5  9.5" (total, unsigned). */
  stripOrderLines: number[];
  stripOrderLineKind: 'spread' | 'total';
  isExpanded: boolean;
  rowStyle: React.CSSProperties | undefined;
  onToggle: () => void;
  /** Fired when an individual pill is clicked. Carries enough context for
   *  the parent to open a TradeModal (or do nothing). */
  onPillTrade: (venue: 'kalshi' | 'polymarket', side: 'yes' | 'no', cents: number) => void;
  yesLabel: string;     // 'Y' for spread, 'O' for total
  noLabel: string;      // 'N' for spread, 'U' for total
}

function ActionRow({
  chips,
  selectedChipIdx,
  onSelectChipIdx,
  stripLabel,
  description,
  kalshiBook,
  polyBook,
  marketSide,
  priceMode,
  stripPosYes,
  stripPosNo,
  stripOrdersYes,
  stripOrdersNo,
  stripOrderLines,
  stripOrderLineKind,
  isExpanded,
  rowStyle,
  onToggle,
  onPillTrade,
  yesLabel,
  noLabel,
}: ActionRowProps) {
  // Match moneyline GameTable: pill price honours the Maker/Taker/Market
  // toolbar toggle via `pillPriceCents`, with NO derived from its own
  // orderbook side (NO ask = 100 − best YES bid) instead of `100 − YES_mid`.
  const yesCents = pillPriceCents(kalshiBook, 'yes', priceMode);
  const noCents = pillPriceCents(kalshiBook, 'no', priceMode);
  const polyYesCents = pillPriceCents(polyBook, 'yes', priceMode);
  const polyNoCents = pillPriceCents(polyBook, 'no', priceMode);

  // Per-user activity rings (matches GameTable's PosCell). 'C' splits the
  // ring by yes/no contract count; 'O' aggregates open-order count by
  // side. Both aggregate across the entire strip — a user's holdings on
  // the over-9.5 line still surface when the over-2.5 chip is selected.
  const posYes = stripPosYes;
  const posNo = stripPosNo;
  const ordersYes = stripOrdersYes;
  const ordersNo = stripOrdersNo;
  const orderTooltipText =
    stripOrderLines.length > 0
      ? `Open orders on ${stripOrderLines.map((l) => formatOrderLine(l, stripOrderLineKind)).join(', ')}`
      : '';

  return (
    <tr
      className={cn(
        // Hover matches the selected-row tint so it's clear the row is a
        // click target. `!` important is load-bearing — alternate rows
        // set background-color via inline `style` (rowStyle), and inline
        // styles outrank plain Tailwind utilities including hover. Same
        // treatment as GameTable's moneyline rows.
        'hover:!bg-black/15 dark:hover:!bg-black/95 cursor-pointer transition-colors',
        isExpanded && 'bg-black/15 dark:bg-black/95',
      )}
      style={rowStyle}
      onClick={onToggle}
    >
      <Td className="text-muted-foreground/70 py-1 text-[10px] uppercase tracking-wider">
        <div className="flex flex-col items-start gap-1">
          <span>{stripLabel}</span>
          {(posYes + posNo > 0 || ordersYes + ordersNo > 0) && (
            <div className="flex items-center gap-1">
              {posYes + posNo > 0 && (
                <span
                  className="inline-flex items-center"
                  title={
                    posYes > 0 && posNo > 0
                      ? `${posYes} YES, ${posNo} NO`
                      : posYes > 0
                        ? `${posYes} YES contract${posYes === 1 ? '' : 's'}`
                        : `${posNo} NO contract${posNo === 1 ? '' : 's'}`
                  }
                  onClick={(e) => e.stopPropagation()}
                >
                  <PosRing yesCount={posYes} noCount={posNo} variant="closed" size={16} />
                </span>
              )}
              {ordersYes + ordersNo > 0 && (
                <span
                  className="inline-flex items-center"
                  title={orderTooltipText}
                  onClick={(e) => e.stopPropagation()}
                >
                  <PosRing yesCount={ordersYes} noCount={ordersNo} variant="open" size={16} />
                </span>
              )}
              {stripOrderLines.length > 0 && (
                <span
                  className="text-muted-foreground/80 font-mono text-[10px] tracking-tight"
                  onClick={(e) => e.stopPropagation()}
                  title={`Open order lines: ${stripOrderLines.map((l) => formatOrderLine(l, stripOrderLineKind)).join(', ')}`}
                >
                  {stripOrderLines.map((l) => formatOrderLine(l, stripOrderLineKind)).join(' ')}
                </span>
              )}
            </div>
          )}
        </div>
      </Td>
      <Td className="py-1">
        <div className="flex flex-col items-start gap-1">
          {description && (
            <div className="text-foreground text-xs font-medium">{description}</div>
          )}
          {chips.length > 0 ? (
            <MiniLineStrip
              chips={chips}
              selectedIdx={selectedChipIdx}
              onSelect={onSelectChipIdx}
            />
          ) : (
            <span className="text-muted-foreground text-xs">—</span>
          )}
        </div>
      </Td>

      <Td className="whitespace-nowrap text-center">
        <div className="flex items-center justify-center gap-1.5">
          <PricePill
            label={yesLabel}
            cents={yesCents}
            side="yes"
            activeMarketSide={marketSide}
            priceMode={priceMode}
            disabled={kalshiBook == null}
            ticker={kalshiBook?.marketTicker ?? null}
            onClick={(e) => {
              e.stopPropagation();
              if (yesCents == null) return;
              onPillTrade('kalshi', 'yes', yesCents);
            }}
          />
          <PricePill
            label={noLabel}
            cents={noCents}
            side="no"
            activeMarketSide={marketSide}
            priceMode={priceMode}
            disabled={kalshiBook == null}
            ticker={kalshiBook?.marketTicker ?? null}
            onClick={(e) => {
              e.stopPropagation();
              if (noCents == null) return;
              onPillTrade('kalshi', 'no', noCents);
            }}
          />
        </div>
      </Td>

      <VolumeSplitCell book={kalshiBook} yesLabel={yesLabel} noLabel={noLabel} />
      <Td className="text-muted-foreground text-right font-mono text-xs">
        {kalshiBook ? formatCompactCurrency(kalshiBook.liquidity) : '—'}
      </Td>

      {/* K Δ/m — 1m best-bid depth velocity on the active side. */}
      {(() => {
        const kTicker = kalshiBook?.marketTicker;
        const kDelta = kTicker
          ? bookDepthVelocity(depthKey(kTicker, marketSide)).m1.depthDelta
          : null;
        return (
          <Td className={cn('text-right font-mono text-xs', depthDeltaTone(kDelta))}>
            {kDelta == null ? '—' : formatSignedVolume(kDelta)}
          </Td>
        );
      })()}

      <Td className="whitespace-nowrap text-center">
        <div className="flex items-center justify-center gap-1.5">
          <PricePill
            label={yesLabel}
            cents={polyYesCents}
            side="yes"
            activeMarketSide={marketSide}
            priceMode={priceMode}
            disabled={polyBook == null}
            ticker={polyBook?.marketTicker ?? null}
            onClick={(e) => {
              e.stopPropagation();
              if (polyYesCents == null) return;
              onPillTrade('polymarket', 'yes', polyYesCents);
            }}
          />
          <PricePill
            label={noLabel}
            cents={polyNoCents}
            side="no"
            activeMarketSide={marketSide}
            priceMode={priceMode}
            disabled={polyBook == null}
            ticker={polyBook?.marketTicker ?? null}
            onClick={(e) => {
              e.stopPropagation();
              if (polyNoCents == null) return;
              onPillTrade('polymarket', 'no', polyNoCents);
            }}
          />
        </div>
      </Td>

      <VolumeSplitCell book={polyBook} yesLabel={yesLabel} noLabel={noLabel} />
      <Td className="text-muted-foreground text-right font-mono text-xs">
        {polyBook ? formatCompactCurrency(polyBook.liquidity) : '—'}
      </Td>

      {/* P Δ/m — Polymarket sibling of K Δ/m. */}
      {(() => {
        const pTicker = polyBook?.marketTicker;
        const pDelta = pTicker
          ? bookDepthVelocity(depthKey(pTicker, marketSide)).m1.depthDelta
          : null;
        return (
          <Td className={cn('text-right font-mono text-xs', depthDeltaTone(pDelta))}>
            {pDelta == null ? '—' : formatSignedVolume(pDelta)}
          </Td>
        );
      })()}
    </tr>
  );
}

// ── VolumeSplitCell ───────────────────────────────────────────────────
// Renders the K Vol / P Vol cell as two stacked per-side numbers:
//   O 23
//   U 45  (for totals — yesLabel='O', noLabel='U')
//   Y 23
//   N 45  (for spreads — yesLabel='Y', noLabel='N')
//
// Each side comes from VenueBook.{yes,no}Volume (resting depth in
// contracts on that side of the book). Falls back to formatting the
// aggregate `volume` when per-side depth is missing (e.g. no orderbook
// frame yet) so existing rows still render rather than going blank.
// Tooltip carries the aggregate cumulative-traded volume from hydra for
// context, since this cell no longer surfaces it directly.
function VolumeSplitCell({
  book,
  yesLabel,
  noLabel,
}: {
  book: VenueBook | null;
  yesLabel: string;
  noLabel: string;
}) {
  if (!book) {
    return (
      <Td className="text-muted-foreground text-right font-mono text-xs">—</Td>
    );
  }
  const hasSplit = book.yesVolume != null && book.noVolume != null;
  return (
    <Td
      className="text-muted-foreground text-right font-mono text-xs"
      title={`Total cumulative volume: ${formatVolume(book.volume)} contracts`}
    >
      {hasSplit ? (
        <div className="flex flex-col items-end gap-px leading-none">
          <span>
            <span className="text-muted-foreground/60 mr-1">{yesLabel}</span>
            {formatVolume(book.yesVolume!)}
          </span>
          <span>
            <span className="text-muted-foreground/60 mr-1">{noLabel}</span>
            {formatVolume(book.noVolume!)}
          </span>
        </div>
      ) : (
        formatVolume(book.volume)
      )}
    </Td>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────

function formatSignedLine(signed: number): string {
  const sign = signed < 0 ? '-' : '+';
  return `${sign}${Math.abs(signed)}`;
}

/** Sport-appropriate unit for the bet-description sentence. */
function sportUnit(sport: Sport): string {
  switch (sport) {
    case 'mlb':
      return 'runs';
    case 'nhl':
      return 'goals';
    case 'atp':
    case 'wta':
      return 'games';
    case 'ufc':
      return 'rounds';
    case 'nba':
    case 'wnba':
    case 'cbb':
    default:
      return 'points';
  }
}

function buildSpreadDescription({
  favoredName,
  mag,
  unit,
}: {
  favoredName: string;
  mag: number;
  unit: string;
}): React.ReactNode {
  return (
    <span>
      {favoredName} wins by over <span className="text-positive">{mag}</span> {unit}
    </span>
  );
}

function buildTotalDescription({
  line,
  unit,
}: {
  line: number;
  unit: string;
}): React.ReactNode {
  return (
    <span>
      Over <span className="text-positive">{line}</span> {unit} scored
    </span>
  );
}

// ── LadderRowsFor — ask/last/bid block below an expanded perspective row ─

interface LadderRowsForProps {
  venueBookKalshi: VenueBook | null;
  venueBookPoly: VenueBook | null;
  flipped: boolean;
  marketSide: MarketSide;
  /** Toolbar price mode (maker/taker/market) — drives the per-row odds fee
   *  perspective, forwarded to each LadderLineRow. */
  priceMode: PriceMode;
  /** Maker-fee rebate fraction (0..1) folded into maker-perspective odds;
   *  0 = none. Forwarded to each LadderLineRow. */
  makerRebatePct: number;
  /** User's open orders on the Kalshi ticker — chips render on the matching
   *  bid/ask row keyed by priceCents. Filtered to the currently-selected
   *  marketSide (same convention as moneyline's GameTable). */
  userOrders: UserOrderSlice[] | null;
  /** Fires when the user clicks a Kalshi or Poly bid/ask price in the
   *  inline ladder. Carries the click price; the parent decides how to
   *  open the trade modal. */
  onLevelClick: (venue: 'kalshi' | 'polymarket', cents: number) => void;
}

function LadderRowsFor({
  venueBookKalshi,
  venueBookPoly,
  flipped,
  marketSide,
  priceMode,
  makerRebatePct,
  userOrders,
  onLevelClick,
}: LadderRowsForProps) {
  // For a flipped row, the toolbar marketSide picks within the row's
  // perspective, which is opposite to the book's. Resolve to the effective
  // side on the BOOK before building the ladder.
  const effectiveMarketSide: MarketSide = flipped
    ? marketSide === 'yes'
      ? 'no'
      : 'yes'
    : marketSide;

  const buildSide = (book: VenueBook | null) => {
    if (!book) return { asks: [] as BookLevel[], bids: [] as BookLevel[] };
    const ownBids = effectiveMarketSide === 'yes' ? book.yes : book.no;
    const oppositeBids = effectiveMarketSide === 'yes' ? book.no : book.yes;
    const lastYes = book.lastPriceCents;
    const ownLast = lastYes == null ? null : effectiveMarketSide === 'yes' ? lastYes : 100 - lastYes;
    const oppositeLast = lastYes == null ? null : effectiveMarketSide === 'yes' ? 100 - lastYes : lastYes;
    return {
      asks: oppositeBids
        .filter(bidIsReal(oppositeLast))
        .slice(0, LADDER_LEVELS)
        .map((l) => ({ priceCents: 100 - l.priceCents, size: l.size })),
      bids: ownBids.filter(bidIsReal(ownLast)).slice(0, LADDER_LEVELS),
    };
  };

  const k = buildSide(venueBookKalshi);
  const p = buildSide(venueBookPoly);

  const kMaxLiq = venueBookKalshi && venueBookKalshi.liquidity > 0 ? venueBookKalshi.liquidity : 1;
  const pMaxLiq = venueBookPoly && venueBookPoly.liquidity > 0 ? venueBookPoly.liquidity : 1;

  const padTo = (rows: BookLevel[]): (BookLevel | null)[] => {
    const out: (BookLevel | null)[] = [...rows];
    while (out.length < LADDER_LEVELS) out.push(null);
    return out;
  };

  // Asks worst-at-top, best-at-bottom near strip; bids best-at-top.
  const kAsksVisual = padTo([...k.asks].reverse());
  const kBidsVisual = padTo(k.bids);
  const pAsksVisual = padTo([...p.asks].reverse());
  const pBidsVisual = padTo(p.bids);

  const lastFor = (book: VenueBook | null): number | null => {
    const ly = book?.lastPriceCents ?? null;
    if (ly == null) return null;
    return effectiveMarketSide === 'yes' ? ly : 100 - ly;
  };

  const kLast = lastFor(venueBookKalshi);
  const pLast = lastFor(venueBookPoly);
  const sideLabel = marketSide === 'yes' ? 'Yes' : 'No';
  const sideColorClass = marketSide === 'yes' ? 'text-positive' : 'text-negative';

  // Per-price-level Kalshi user-order chips — same pattern as moneyline's
  // GameTable. BUY orders ride bid rows; SELL orders ride ask rows. Filter
  // to the side the ladder is currently expanded for so chips don't bleed
  // between yes/no toggles.
  const buildOrdersByCents = (action: 'buy' | 'sell') => {
    const map = new Map<number, BidRowOrder[]>();
    if (!userOrders || !venueBookKalshi) return map;
    for (const o of userOrders) {
      if (o.action !== action) continue;
      if (o.side !== effectiveMarketSide) continue;
      const depth = queueDepth(o, venueBookKalshi);
      const list = map.get(o.priceCents) ?? [];
      list.push({
        orderId: o.orderId,
        remainingCount: o.remainingCount,
        queueOrBetter: depth.atLevelOrBetter,
        tooltip: orderTooltip(o, venueBookKalshi),
      });
      map.set(o.priceCents, list);
    }
    for (const list of map.values()) {
      list.sort((a, b) => b.remainingCount - a.remainingCount);
    }
    return map;
  };
  const kBuyOrdersByCents = buildOrdersByCents('buy');
  const kSellOrdersByCents = buildOrdersByCents('sell');

  return (
    <>
      {kAsksVisual.map((kLvl, idx) => (
        <LadderLineRow
          key={`ask-${idx}`}
          kLevel={kLvl}
          pLevel={pAsksVisual[idx] ?? null}
          kMaxLiq={kMaxLiq}
          pMaxLiq={pMaxLiq}
          kTicker={venueBookKalshi?.marketTicker ?? null}
          pTicker={venueBookPoly?.marketTicker ?? null}
          kLastCents={kLast}
          pLastCents={pLast}
          kind="ask"
          marketSide={marketSide}
          priceMode={priceMode}
          makerRebatePct={makerRebatePct}
          kUserOrders={kLvl ? kSellOrdersByCents.get(kLvl.priceCents) : undefined}
          onKalshiClick={(cents) => onLevelClick('kalshi', cents)}
          onPolyClick={(cents) => onLevelClick('polymarket', cents)}
        />
      ))}

      {/* Last-trade strip removed — the matching row now wears an
          animate-last-trade-pulse grey halo. */}

      {/* Asks ↔ bids separator. Same faint hairline the moneyline
          ladder uses; colSpan oversized to safely cover every column
          layout this table renders in. */}
      <tr aria-hidden="true">
        <td colSpan={12} className="border-border/40 border-t p-0" />
      </tr>

      {kBidsVisual.map((kLvl, idx) => (
        <LadderLineRow
          key={`bid-${idx}`}
          kLevel={kLvl}
          pLevel={pBidsVisual[idx] ?? null}
          kMaxLiq={kMaxLiq}
          pMaxLiq={pMaxLiq}
          kTicker={venueBookKalshi?.marketTicker ?? null}
          pTicker={venueBookPoly?.marketTicker ?? null}
          kLastCents={kLast}
          pLastCents={pLast}
          kind="bid"
          marketSide={marketSide}
          priceMode={priceMode}
          makerRebatePct={makerRebatePct}
          kUserOrders={kLvl ? kBuyOrdersByCents.get(kLvl.priceCents) : undefined}
          onKalshiClick={(cents) => onLevelClick('kalshi', cents)}
          onPolyClick={(cents) => onLevelClick('polymarket', cents)}
        />
      ))}
    </>
  );
}

// ── LadderLineRow — one ask or bid row across both venues ─────────────

interface LadderLineRowProps {
  kLevel: BookLevel | null;
  pLevel: BookLevel | null;
  kMaxLiq: number;
  pMaxLiq: number;
  kTicker: string | null;
  pTicker: string | null;
  /** Side-native last trade for each venue book. Drives the slow grey
   *  pulse on whichever row's price matches it (replaces the removed
   *  per-venue "Trade {Side} / Last X¢" strip row). */
  kLastCents?: number | null;
  pLastCents?: number | null;
  kind: 'ask' | 'bid';
  /** Active Y/N toggle. Price cells align under the matching pill — left
   *  (under Y) for 'yes', right (under N) for 'no' — so the book hangs
   *  beneath whichever side is selected. */
  marketSide: MarketSide;
  /** Toolbar price mode (maker/taker/market) — drives the fee perspective
   *  for this row's odds, same mapping the Y/N pills use. */
  priceMode: PriceMode;
  /** Maker-fee rebate fraction (0..1) folded into maker-perspective odds;
   *  0 = none. */
  makerRebatePct: number;
  /** User open orders matching this Kalshi row's price (qty + queue, qty
   *  desc). Now rendered in the freed-up Side & Line column (was the
   *  Ask/Bid label) as inert chips; clicking the price still opens the
   *  trade modal. */
  kUserOrders?: BidRowOrder[];
  /** Click handler for the Kalshi price cell. Fires with the level's
   *  cents value so the parent can open a trade modal prefilled to that
   *  price. */
  onKalshiClick: (cents: number) => void;
  onPolyClick: (cents: number) => void;
}

function LadderLineRow({
  kLevel,
  pLevel,
  kMaxLiq,
  pMaxLiq,
  kTicker,
  pTicker,
  kLastCents,
  pLastCents,
  kind,
  marketSide,
  priceMode,
  makerRebatePct,
  kUserOrders,
  onKalshiClick,
  onPolyClick,
}: LadderLineRowProps) {
  const hasUserOrders = kUserOrders && kUserOrders.length > 0;
  const kLiq = kLevel ? Math.round((kLevel.priceCents * kLevel.size) / 100) : 0;
  const pLiq = pLevel ? Math.round((pLevel.priceCents * pLevel.size) / 100) : 0;
  const colorClass = kind === 'ask' ? 'text-negative' : 'text-positive';
  const kIsLastTrade = kLastCents != null && kLevel?.priceCents === kLastCents;
  const pIsLastTrade = pLastCents != null && pLevel?.priceCents === pLastCents;

  const feeAdjusted = useDisplaySettings((s) => s.feeAdjustedOdds);
  const kFeeEntry = useFeeFor(kTicker);
  const pFeeEntry = useFeeFor(pTicker);
  // Fee perspective follows the table's price-mode toggle (Maker/Taker/
  // Market) — same mapping the moneyline ladder + Y/N pills use. A resting
  // order is always a maker regardless of bid/ask side, so the perspective
  // is NOT derived from the row kind.
  const oddsPerspective: FeePerspective = priceMode === 'maker' ? 'maker' : 'taker';
  const kEffectiveCents = kLevel
    ? effectivePriceForOdds(kLevel.priceCents, feeAdjusted, kFeeEntry, oddsPerspective, makerRebatePct)
    : null;
  const pEffectiveCents = pLevel
    ? effectivePriceForOdds(pLevel.priceCents, feeAdjusted, pFeeEntry, oddsPerspective, makerRebatePct)
    : null;
  const kOdds = kEffectiveCents != null ? americanOdds(kEffectiveCents) : null;
  const pOdds = pEffectiveCents != null ? americanOdds(pEffectiveCents) : null;

  return (
    <tr className="border-0">
      <Td className="py-0.5"> </Td>
      {/* Your active Kalshi orders at this price level (qty / queue-or-better,
          qty desc) — this is the Side & Line column the Ask/Bid label used to
          occupy. Direction is conveyed by the price color and which half of
          the ladder (above/below the spread hairline) the row sits in, so the
          label was redundant. Right-aligned, flush against the price column;
          inert (clicks stop here). */}
      <Td className="py-0.5 text-right">
        {hasUserOrders ? (
          <div
            className="text-foreground/90 flex items-center justify-end gap-1.5 overflow-hidden text-[10px]"
            onClick={(e) => e.stopPropagation()}
          >
            {kUserOrders!.map((o, i) => (
              <Fragment key={o.orderId}>
                {i > 0 && <span className="bg-muted-foreground/40 h-3 w-px shrink-0 self-center" />}
                {/* Parity with GameTable's LadderRow chips: qty stays
                    neutral (user's own size), queueOrBetter wears the
                    absolute queueTone (<50k green, 50–300k yellow,
                    ≥300k red) so the spread/total ladder reads the
                    same at-a-glance signal as the moneyline ladder. */}
                <span className="truncate" title={o.tooltip}>
                  <span>{Math.round(o.remainingCount)}/</span>
                  <span className={queueTone(o.queueOrBetter, null)}>
                    {o.queueOrBetter != null ? formatVolume(Math.round(o.queueOrBetter)) : '?'}
                  </span>
                </span>
              </Fragment>
            ))}
          </div>
        ) : (
          ''
        )}
      </Td>
      {/* Kalshi price + American odds. Aligns under the active pill — left
          (under Y) for 'yes', right (under N) for 'no'. */}
      <Td
        className={cn(
          'whitespace-nowrap py-0.5 font-mono',
          marketSide === 'yes' ? 'text-left' : 'text-right',
        )}
      >
        {kLevel ? (
          <span
            className={cn(
              'inline-flex cursor-pointer items-center gap-1 px-1 hover:underline',
              kIsLastTrade && 'animate-last-trade-pulse',
            )}
            onClick={(e) => {
              e.stopPropagation();
              onKalshiClick(kLevel.priceCents);
            }}
            title={kIsLastTrade ? `Last trade @ ${kLevel.priceCents}¢` : `Click to trade @ ${kLevel.priceCents}¢`}
          >
            <span className={cn(colorClass)}>{kLevel.priceCents}¢</span>
            {kOdds && (
              <>
                <span className="text-muted-foreground/40 text-xs">|</span>
                <span className="text-foreground text-xs">{kOdds}</span>
              </>
            )}
          </span>
        ) : null}
      </Td>
      <Td className="text-muted-foreground py-0.5 text-right font-mono text-xs">
        {kLevel ? formatVolume(kLevel.size) : ''}
      </Td>
      <Td className="py-0.5">
        <LiquidityBar value={kLiq} maxValue={kMaxLiq} height={16} />
      </Td>
      {/* K Δ/m spacer — velocity column lives on the action row. */}
      <Td className="py-0.5">{' '}</Td>
      <Td
        className={cn(
          'whitespace-nowrap py-0.5 font-mono',
          marketSide === 'yes' ? 'text-left' : 'text-right',
        )}
      >
        {pLevel ? (
          <span
            className={cn(
              'inline-flex cursor-pointer items-center gap-1 px-1 hover:underline',
              pIsLastTrade && 'animate-last-trade-pulse',
            )}
            onClick={(e) => {
              e.stopPropagation();
              onPolyClick(pLevel.priceCents);
            }}
            title={pIsLastTrade ? `Last trade @ ${pLevel.priceCents}¢` : `Click to trade @ ${pLevel.priceCents}¢`}
          >
            <span className={cn(colorClass)}>{pLevel.priceCents}¢</span>
            {pOdds && (
              <>
                <span className="text-muted-foreground/40 text-xs">|</span>
                <span className="text-foreground text-xs">{pOdds}</span>
              </>
            )}
          </span>
        ) : null}
      </Td>
      <Td className="text-muted-foreground py-0.5 text-right font-mono text-xs">
        {pLevel ? formatVolume(pLevel.size) : ''}
      </Td>
      <Td className="py-0.5">
        <LiquidityBar value={pLiq} maxValue={pMaxLiq} height={16} />
      </Td>
      {/* P Δ/m spacer — velocity column lives on the action row. */}
      <Td className="py-0.5">{' '}</Td>
    </tr>
  );
}
